import type { Overview, Position, AssetInput } from '@/features/wealth/model'
import axios, { AxiosInstance, AxiosError } from 'axios'
import type {
  User,
  LoginRequest,
  RegisterRequest,
  TokenResponse,
  Portfolio,
  PortfolioSummary,
  Holding,
  Transaction,
  TransactionCreate,
  PerformanceMetrics,
  CapTable,
  InvestmentRound,
  Shareholder,
  EquityGrant,
  WaterfallResult,
  Account,
  AccountSyncStatus,
  Asset,
  PriceData,
  PriceHistory,
  CurrencyRate,
  UpdatePricesResult,
  SecuritySearchResult,
  TaxSummary,
  RealizedGain,
  UnrealizedGain,
  RealEstateProperty,
  RealEstateCreate,
  RealEstateUpdate,
  RealEstateSummary,
  AdminUser,
  AdminPortfolio,
  AdminStats,
  ScreenerItem,
  ScreenerResponse,
  ScreenerSummary,
  ScreenerFilters,
  PublicPortfolioListItem,
  PublicPortfolioDetail,
  PublicPortfolioSettings,
  PortfolioPublicInfo,
  Article,
  ArticleListResponse,
  ArticleCreate,
  ArticleUpdate,
  ArticleCategory,
  ArticleStatus,
  Comment,
  CommentCreate,
  LikeResponse,
  FollowResponse,
  UserProfile,
  DividendCalendarResponse,
  MonthlyDividendResponse,
  Plan,
  Subscription,
  SubscriptionSummary,
  Payment,
  PaymentListResponse,
  BillingPeriod,
  CloudPaymentsCheckoutData,
  OtherAsset,
  OtherAssetWithIncome,
  OtherAssetCreate,
  OtherAssetUpdate,
  OtherAssetsSummary,
  OtherAssetCategory,
  IncomeRecord,
  IncomeRecordCreate,
  CategorySubcategories,
  Pipeline,
  PipelineDetail,
  PipelineCreate,
  PipelineUpdate,
  PipelineStage,
  PipelineStageCreate,
  PipelineStageUpdate,
  PipelineAccess,
  PipelineAccessCreate,
  AccessLevel,
  PipelineStats,
  KanbanBoard,
  Deal,
  DealDetail,
  DealCreate,
  DealUpdate,
  DealMoveRequest,
  DealListResponse,
  DealStatus,
  DealContact,
  DealContactCreate,
  DealDocument,
  DealDocumentCreate,
  DealComment,
  DealCommentCreate,
  DealActivity,
  ImportResult,
} from '@/types'

const API_URL = '/api/v1'

class ApiService {
  private client: AxiosInstance
  private refreshPromise: Promise<TokenResponse> | null = null

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor to add auth token
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    // Response interceptor for token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as (typeof error.config & { _retry?: boolean })
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry && (!originalRequest.url?.startsWith('/auth/') || originalRequest.url === '/auth/me')) {
          originalRequest._retry = true
          try {
            const tokens = await this.refreshToken()
            localStorage.setItem('access_token', tokens.access_token)
            localStorage.setItem('refresh_token', tokens.refresh_token)
            originalRequest.headers.Authorization = `Bearer ${tokens.access_token}`
            return this.client(originalRequest)
          } catch {
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            window.location.href = '/login'
          }
        }
        return Promise.reject(error)
      }
    )
  }

  async getWealthOverview(): Promise<Overview> {
    return (await this.client.get<Overview>('/wealth/overview')).data
  }
  async createWealthAsset(data: AssetInput): Promise<Position> {
    return (await this.client.post<Position>('/wealth/assets', data)).data
  }
  async getWealthAsset(id: number): Promise<AssetInput> {
    const data = (await this.client.get<AssetInput>(`/wealth/assets/${id}`)).data
    return { ...data, value: data.value === null ? null : Number(data.value), debt: Number(data.debt), monthly_income: Number(data.monthly_income), monthly_costs: Number(data.monthly_costs), monthly_tax: Number(data.monthly_tax), monthly_debt_payment: Number(data.monthly_debt_payment) }
  }
  async updateWealthAsset(id: number, data: AssetInput): Promise<Position> {
    return (await this.client.put<Position>(`/wealth/assets/${id}`, data)).data
  }

  // Auth
  async login(data: LoginRequest): Promise<TokenResponse> {
    const response = await this.client.post<TokenResponse>('/auth/login', data)
    return response.data
  }

  async register(data: RegisterRequest): Promise<User> {
    const response = await this.client.post<User>('/auth/register', data)
    return response.data
  }

  async refreshToken(): Promise<TokenResponse> {
    if (this.refreshPromise) {
      return this.refreshPromise
    }

    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) {
      throw new Error('No refresh token')
    }

    this.refreshPromise = this.client
      .post<TokenResponse>('/auth/refresh', { refresh_token: refreshToken })
      .then((res) => res.data)
      .finally(() => {
        this.refreshPromise = null
      })

    return this.refreshPromise
  }

  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem('refresh_token')
    if (refreshToken) {
      await this.client.post('/auth/logout', { refresh_token: refreshToken })
    }
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }

  async getMe(): Promise<User> {
    const response = await this.client.get<User>('/auth/me')
    return response.data
  }

  async enable2FA(password: string): Promise<{ secret: string; qr_code: string }> {
    const response = await this.client.post('/auth/2fa/enable', { password })
    return response.data
  }

  async verify2FA(code: string): Promise<void> {
    await this.client.post('/auth/2fa/verify', { code })
  }

  async disable2FA(code: string): Promise<void> {
    await this.client.post('/auth/2fa/disable', { code })
  }

  // Portfolios
  async getPortfolios(): Promise<Portfolio[]> {
    const response = await this.client.get<Portfolio[]>('/portfolios')
    return response.data
  }

  async createPortfolio(data: Partial<Portfolio>): Promise<Portfolio> {
    const response = await this.client.post<Portfolio>('/portfolios', data)
    return response.data
  }

  async getPortfolio(id: number): Promise<Portfolio> {
    const response = await this.client.get<Portfolio>(`/portfolios/${id}`)
    return response.data
  }

  async updatePortfolio(id: number, data: Partial<Portfolio>): Promise<Portfolio> {
    const response = await this.client.put<Portfolio>(`/portfolios/${id}`, data)
    return response.data
  }

  async deletePortfolio(id: number): Promise<void> {
    await this.client.delete(`/portfolios/${id}`)
  }

  async getPortfolioSummary(id: number): Promise<PortfolioSummary> {
    const response = await this.client.get<PortfolioSummary>(`/portfolios/${id}/summary`)
    return response.data
  }

  async getPortfolioHoldings(id: number): Promise<Holding[]> {
    const response = await this.client.get<Holding[]>(`/portfolios/${id}/holdings`)
    return response.data
  }

  async getPortfolioTransactions(
    id: number,
    params?: { skip?: number; limit?: number; asset_id?: number; transaction_type?: string }
  ): Promise<Transaction[]> {
    const response = await this.client.get<Transaction[]>(`/portfolios/${id}/transactions`, {
      params,
    })
    return response.data
  }

  async getPortfolioPerformance(id: number, period: string = '1y'): Promise<PerformanceMetrics> {
    const response = await this.client.get<PerformanceMetrics>(
      `/portfolios/${id}/performance`,
      { params: { period } }
    )
    return response.data
  }

  // Transactions
  async createTransaction(portfolioId: number, data: TransactionCreate): Promise<Transaction> {
    const response = await this.client.post<Transaction>(
      `/transactions?portfolio_id=${portfolioId}`,
      data
    )
    return response.data
  }

  async getTransaction(id: number): Promise<Transaction> {
    const response = await this.client.get<Transaction>(`/transactions/${id}`)
    return response.data
  }

  async updateTransaction(id: number, data: Partial<TransactionCreate>): Promise<Transaction> {
    const response = await this.client.put<Transaction>(`/transactions/${id}`, data)
    return response.data
  }

  async deleteTransaction(id: number): Promise<void> {
    await this.client.delete(`/transactions/${id}`)
  }

  async importTransactions(
    portfolioId: number,
    broker: string,
    fileContent: string,
    fileType: string
  ): Promise<{ imported_count: number; skipped_count: number; errors: string[] }> {
    const response = await this.client.post(
      `/transactions/import?portfolio_id=${portfolioId}`,
      { broker, file_content: fileContent, file_type: fileType }
    )
    return response.data
  }

  // Cap Tables
  async getCapTables(): Promise<CapTable[]> {
    const response = await this.client.get<CapTable[]>('/captables')
    return response.data
  }

  async createCapTable(portfolioId: number, data: Partial<CapTable>): Promise<CapTable> {
    const response = await this.client.post<CapTable>(
      `/captables?portfolio_id=${portfolioId}`,
      data
    )
    return response.data
  }

  async getCapTable(id: number): Promise<CapTable> {
    const response = await this.client.get<CapTable>(`/captables/${id}`)
    return response.data
  }

  async updateCapTable(id: number, data: Partial<CapTable>): Promise<CapTable> {
    const response = await this.client.put<CapTable>(`/captables/${id}`, data)
    return response.data
  }

  async deleteCapTable(id: number): Promise<void> {
    await this.client.delete(`/captables/${id}`)
  }

  async addRound(capTableId: number, round: InvestmentRound): Promise<CapTable> {
    const response = await this.client.post<CapTable>(`/captables/${capTableId}/rounds`, {
      round,
    })
    return response.data
  }

  async getWaterfall(
    capTableId: number,
    exitValue: number,
    exitType: string = 'acquisition'
  ): Promise<WaterfallResult> {
    const response = await this.client.get<WaterfallResult>(
      `/captables/${capTableId}/waterfall`,
      { params: { exit_value: exitValue, exit_type: exitType } }
    )
    return response.data
  }

  async getShareholders(capTableId: number): Promise<Shareholder[]> {
    const response = await this.client.get<Shareholder[]>(
      `/captables/${capTableId}/shareholders`
    )
    return response.data
  }

  async createShareholder(capTableId: number, data: Partial<Shareholder>): Promise<Shareholder> {
    const response = await this.client.post<Shareholder>(
      `/captables/${capTableId}/shareholders`,
      data
    )
    return response.data
  }

  async createRound(capTableId: number, round: {
    name: string
    date: string
    pre_money: number
    amount_raised: number
    share_class?: string
    liquidation_preference?: number
    participating?: boolean
    participation_cap?: number
    anti_dilution?: string
  }): Promise<CapTable> {
    const response = await this.client.post<CapTable>(
      `/captables/${capTableId}/rounds`,
      { round }
    )
    return response.data
  }

  async getGrants(capTableId: number): Promise<EquityGrant[]> {
    const response = await this.client.get<EquityGrant[]>(`/captables/${capTableId}/grants`)
    return response.data
  }

  async createGrant(capTableId: number, data: Partial<EquityGrant>): Promise<EquityGrant> {
    const response = await this.client.post<EquityGrant>(
      `/captables/${capTableId}/grants`,
      data
    )
    return response.data
  }

  async updateGrant(
    capTableId: number,
    grantId: number,
    data: Partial<EquityGrant>
  ): Promise<EquityGrant> {
    const response = await this.client.put<EquityGrant>(
      `/captables/${capTableId}/grants/${grantId}`,
      data
    )
    return response.data
  }

  // Accounts
  async getAccounts(): Promise<Account[]> {
    const response = await this.client.get<Account[]>('/accounts')
    return response.data
  }

  async createAccount(data: Partial<Account>): Promise<Account> {
    const response = await this.client.post<Account>('/accounts', data)
    return response.data
  }

  async deleteAccount(id: number): Promise<void> {
    await this.client.delete(`/accounts/${id}`)
  }

  async syncAccount(id: number, force: boolean = false): Promise<AccountSyncStatus> {
    const response = await this.client.post<AccountSyncStatus>(`/accounts/${id}/sync`, {
      force,
    })
    return response.data
  }

  async getAccountStatus(id: number): Promise<AccountSyncStatus> {
    const response = await this.client.get<AccountSyncStatus>(`/accounts/${id}/status`)
    return response.data
  }

  // Assets
  async getAssets(params?: { search?: string; asset_type?: string }): Promise<Asset[]> {
    const response = await this.client.get<Asset[]>('/assets', { params })
    return response.data
  }

  async createAsset(data: Partial<Asset>): Promise<Asset> {
    const response = await this.client.post<Asset>('/assets', data)
    return response.data
  }

  async getAsset(id: number): Promise<Asset> {
    const response = await this.client.get<Asset>(`/assets/${id}`)
    return response.data
  }

  // Prices
  async getMoexPrice(ticker: string, market: string = 'shares'): Promise<PriceData> {
    const response = await this.client.get<PriceData>(`/prices/moex/${ticker}`, {
      params: { market },
    })
    return response.data
  }

  async getCryptoPrice(coinId: string): Promise<PriceData> {
    const response = await this.client.get<PriceData>(`/prices/crypto/${coinId}`)
    return response.data
  }

  async getCurrencyRate(currency: string): Promise<CurrencyRate> {
    const response = await this.client.get<CurrencyRate>(`/prices/currency/${currency}`)
    return response.data
  }

  async getAssetPrice(assetId: number): Promise<PriceData> {
    const response = await this.client.get<PriceData>(`/prices/asset/${assetId}`)
    return response.data
  }

  async getAssetPriceHistory(assetId: number, days: number = 30): Promise<PriceHistory[]> {
    const response = await this.client.get<PriceHistory[]>(`/prices/asset/${assetId}/history`, {
      params: { days },
    })
    return response.data
  }

  async updatePortfolioPrices(portfolioId: number): Promise<UpdatePricesResult> {
    const response = await this.client.post<UpdatePricesResult>(
      `/prices/portfolio/${portfolioId}/update`
    )
    return response.data
  }

  async searchSecurities(query: string): Promise<SecuritySearchResult[]> {
    const response = await this.client.get<SecuritySearchResult[]>(`/prices/search/${query}`)
    return response.data
  }

  // Tax
  async getTaxSummary(portfolioId: number, year: number): Promise<TaxSummary> {
    const response = await this.client.get<TaxSummary>(
      `/tax/portfolio/${portfolioId}/summary`,
      { params: { year } }
    )
    return response.data
  }

  async getRealizedGains(portfolioId: number, year?: number): Promise<RealizedGain[]> {
    const response = await this.client.get<RealizedGain[]>(
      `/tax/portfolio/${portfolioId}/realized`,
      { params: year ? { year } : {} }
    )
    return response.data
  }

  async getUnrealizedGains(portfolioId: number): Promise<UnrealizedGain[]> {
    const response = await this.client.get<UnrealizedGain[]>(
      `/tax/portfolio/${portfolioId}/unrealized`
    )
    return response.data
  }

  // Real Estate
  async getRealEstateProperties(portfolioId: number): Promise<RealEstateProperty[]> {
    const response = await this.client.get<RealEstateProperty[]>(
      `/realestate/portfolio/${portfolioId}`
    )
    return response.data
  }

  async getRealEstateSummary(portfolioId: number): Promise<RealEstateSummary> {
    const response = await this.client.get<RealEstateSummary>(
      `/realestate/portfolio/${portfolioId}/summary`
    )
    return response.data
  }

  async createRealEstateProperty(
    portfolioId: number,
    data: RealEstateCreate
  ): Promise<RealEstateProperty> {
    const response = await this.client.post<RealEstateProperty>(
      `/realestate/portfolio/${portfolioId}`,
      data
    )
    return response.data
  }

  async getRealEstateProperty(id: number): Promise<RealEstateProperty> {
    const response = await this.client.get<RealEstateProperty>(`/realestate/${id}`)
    return response.data
  }

  async updateRealEstateProperty(
    id: number,
    data: RealEstateUpdate
  ): Promise<RealEstateProperty> {
    const response = await this.client.put<RealEstateProperty>(`/realestate/${id}`, data)
    return response.data
  }

  async deleteRealEstateProperty(id: number): Promise<void> {
    await this.client.delete(`/realestate/${id}`)
  }

  // Admin
  async getAdminStats(): Promise<AdminStats> {
    const response = await this.client.get<AdminStats>('/admin/stats')
    return response.data
  }

  async getAdminUsers(params?: { skip?: number; limit?: number }): Promise<AdminUser[]> {
    const response = await this.client.get<AdminUser[]>('/admin/users', { params })
    return response.data
  }

  async getAdminUser(id: number): Promise<AdminUser> {
    const response = await this.client.get<AdminUser>(`/admin/users/${id}`)
    return response.data
  }

  async updateAdminUser(
    id: number,
    data: { is_active?: boolean; is_verified?: boolean; is_admin?: boolean }
  ): Promise<AdminUser> {
    const response = await this.client.patch<AdminUser>(`/admin/users/${id}`, data)
    return response.data
  }

  async getAdminPortfolios(params?: { skip?: number; limit?: number; user_id?: number }): Promise<AdminPortfolio[]> {
    const response = await this.client.get<AdminPortfolio[]>('/admin/portfolios', { params })
    return response.data
  }

  async getAdminPortfolio(id: number): Promise<any> {
    const response = await this.client.get(`/admin/portfolios/${id}`)
    return response.data
  }

  async transferPortfolio(portfolioId: number, targetUserId: number): Promise<any> {
    const response = await this.client.post(`/admin/portfolios/${portfolioId}/transfer`, null, {
      params: { target_user_id: targetUserId }
    })
    return response.data
  }

  // Import
  async getSupportedBrokers(): Promise<{ brokers: { name: string; extensions: string[] }[] }> {
    const response = await this.client.get('/import/supported-brokers')
    return response.data
  }

  async importBrokerReport(
    portfolioId: number,
    file: File,
    broker?: string
  ): Promise<{
    success: boolean
    broker: string
    holdings_created: number
    holdings_updated: number
    transactions_created: number
    assets_created: number
    errors: string[]
  }> {
    const formData = new FormData()
    formData.append('file', file)
    if (broker) {
      formData.append('broker', broker)
    }

    const response = await this.client.post(`/import/upload/${portfolioId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  async previewImport(
    portfolioId: number,
    file: File,
    broker?: string
  ): Promise<{
    broker: string
    holdings: {
      ticker: string
      isin: string | null
      name: string
      quantity: number
      average_price: number
      currency: string
      asset_type: string
    }[]
    transactions: {
      ticker: string
      name: string
      type: string
      quantity: number
      price: number
      total_amount: number
      currency: string
      executed_at: string
      fees: number
    }[]
    errors: string[]
  }> {
    const formData = new FormData()
    formData.append('file', file)
    if (broker) {
      formData.append('broker', broker)
    }

    const response = await this.client.post(`/import/preview/${portfolioId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  // Analytics
  async getPortfolioHistory(
    portfolioId: number,
    days: number = 30
  ): Promise<{
    portfolio_id: number
    portfolio_name: string
    snapshots: {
      date: string
      total_value: number
      cost_basis: number | null
      unrealized_pnl: number | null
      breakdown: Record<string, number> | null
    }[]
    current_value: number
    current_cost_basis: number
    total_return: number
    total_return_percent: number
  }> {
    const response = await this.client.get(`/analytics/portfolio/${portfolioId}/history`, {
      params: { days },
    })
    return response.data
  }

  async getPortfolioAllocation(portfolioId: number): Promise<{
    portfolio_id: number
    total_value: number
    by_asset_type: {
      name: string
      ticker: string | null
      value: number
      weight: number
      asset_type: string
    }[]
    by_holding: {
      name: string
      ticker: string | null
      value: number
      weight: number
      asset_type: string
    }[]
  }> {
    const response = await this.client.get(`/analytics/portfolio/${portfolioId}/allocation`)
    return response.data
  }

  async getDashboardSummary(): Promise<{
    portfolio_count: number
    total_value: number
    total_cost_basis: number
    total_return: number
    total_return_percent: number
  }> {
    const response = await this.client.get('/analytics/dashboard/summary')
    return response.data
  }

  async getEnhancedHoldings(portfolioId: number): Promise<{
    portfolio_id: number
    portfolio_name: string
    total_value: number
    total_cost_basis: number
    total_unrealized_pnl: number
    total_unrealized_pnl_percent: number
    total_dividends_ytd: number
    total_dividends_all_time: number
    holdings: {
      id: number
      portfolio_id: number
      account_id: number | null
      asset_id: number
      asset: {
        id: number
        type: string
        ticker: string | null
        isin: string | null
        figi: string | null
        name: string
        currency: string
        exchange: string | null
        sector: string | null
        country: string | null
        logo_url: string | null
        created_at: string
      }
      quantity: number
      average_cost: number | null
      current_price: number | null
      current_value: number | null
      cost_basis: number | null
      unrealized_pnl: number | null
      unrealized_pnl_percent: number | null
      weight: number | null
      created_at: string
      updated_at: string
      dividends_total: number
      dividends_ytd: number
      dividend_yield: number | null
      last_dividend_date: string | null
      next_dividend_date: string | null
      annual_dividend_per_share: number | null
    }[]
  }> {
    const response = await this.client.get(`/analytics/portfolio/${portfolioId}/holdings/enhanced`)
    return response.data
  }

  // Deposit Comparison
  async getDepositComparison(): Promise<{
    key_rate: number
    deposit_rate: number
    deposit_rate_range: string
    alternatives: {
      id: string
      name_ru: string
      name_en: string
      yield_pct: number
      yield_range: string | null
      risk_level: number
      risk_label_ru: string
      risk_label_en: string
      description_ru: string
      description_en: string
      badge: string | null
      liquidity_ru: string
      liquidity_en: string
      min_amount: number
      horizon_ru: string
      horizon_en: string
      tax_note_ru: string | null
      tax_note_en: string | null
    }[]
    updated_at: string
  }> {
    const response = await this.client.get('/analytics/deposit-comparison')
    return response.data
  }

  // Screener
  async screenDividendStocks(filters?: ScreenerFilters): Promise<ScreenerResponse> {
    const response = await this.client.get<ScreenerResponse>('/screener', { params: filters })
    return response.data
  }

  async getScreenerSummary(): Promise<ScreenerSummary> {
    const response = await this.client.get<ScreenerSummary>('/screener/summary')
    return response.data
  }

  async getTopYieldStocks(limit: number = 10, currency?: string): Promise<ScreenerItem[]> {
    const response = await this.client.get<ScreenerItem[]>('/screener/top-yield', {
      params: { limit, currency },
    })
    return response.data
  }

  async getTopGrowthStocks(limit: number = 10, currency?: string): Promise<ScreenerItem[]> {
    const response = await this.client.get<ScreenerItem[]>('/screener/top-growth', {
      params: { limit, currency },
    })
    return response.data
  }

  async getTopRatedStocks(limit: number = 10, minRating: number = 4, currency?: string): Promise<ScreenerItem[]> {
    const response = await this.client.get<ScreenerItem[]>('/screener/top-rated', {
      params: { limit, min_rating: minRating, currency },
    })
    return response.data
  }

  async getDividendAristocrats(limit: number = 50): Promise<ScreenerItem[]> {
    const response = await this.client.get<ScreenerItem[]>('/screener/aristocrats', {
      params: { limit },
    })
    return response.data
  }

  // Public Portfolios (No Auth Required)
  async getPublicPortfolios(params?: {
    limit?: number
    offset?: number
    sort_by?: 'published_at' | 'name' | 'holdings_count'
    sort_order?: 'asc' | 'desc'
  }): Promise<PublicPortfolioListItem[]> {
    const response = await this.client.get<PublicPortfolioListItem[]>('/public/portfolios', {
      params,
    })
    return response.data
  }

  async getPublicPortfolio(slug: string): Promise<PublicPortfolioDetail> {
    const response = await this.client.get<PublicPortfolioDetail>(`/public/portfolios/${slug}`)
    return response.data
  }

  async checkPublicPortfolioExists(slug: string): Promise<{ exists: boolean; slug: string }> {
    const response = await this.client.get<{ exists: boolean; slug: string }>(
      `/public/portfolios/${slug}/check`
    )
    return response.data
  }

  // Public Portfolio Settings (Auth Required)
  async getMyPortfoliosPublicSettings(): Promise<PortfolioPublicInfo[]> {
    const response = await this.client.get<PortfolioPublicInfo[]>(
      '/public/my-portfolios/public-settings'
    )
    return response.data
  }

  async updatePortfolioPublicSettings(
    portfolioId: number,
    settings: PublicPortfolioSettings
  ): Promise<Portfolio> {
    const response = await this.client.put<Portfolio>(
      `/public/my-portfolios/${portfolioId}/public-settings`,
      settings
    )
    return response.data
  }

  async generatePortfolioSlug(portfolioId: number): Promise<{ slug: string }> {
    const response = await this.client.post<{ slug: string }>(
      `/public/my-portfolios/${portfolioId}/generate-slug`
    )
    return response.data
  }

  async checkSlugAvailability(
    slug: string,
    portfolioId?: number
  ): Promise<{ slug: string; available: boolean }> {
    const response = await this.client.get<{ slug: string; available: boolean }>(
      `/public/check-slug/${slug}`,
      { params: portfolioId ? { portfolio_id: portfolioId } : {} }
    )
    return response.data
  }

  // Community - Articles
  async getArticles(params?: {
    category?: ArticleCategory
    author_id?: number
    search?: string
    page?: number
    page_size?: number
  }): Promise<ArticleListResponse> {
    const response = await this.client.get<ArticleListResponse>('/community/articles', { params })
    return response.data
  }

  async getArticle(slug: string): Promise<Article> {
    const response = await this.client.get<Article>(`/community/articles/${slug}`)
    return response.data
  }

  async getMyArticles(params?: {
    status?: ArticleStatus
    page?: number
    page_size?: number
  }): Promise<ArticleListResponse> {
    const response = await this.client.get<ArticleListResponse>('/community/my-articles', { params })
    return response.data
  }

  async createArticle(data: ArticleCreate): Promise<Article> {
    const response = await this.client.post<Article>('/community/articles', data)
    return response.data
  }

  async updateArticle(articleId: number, data: ArticleUpdate): Promise<Article> {
    const response = await this.client.put<Article>(`/community/articles/${articleId}`, data)
    return response.data
  }

  async deleteArticle(articleId: number): Promise<void> {
    await this.client.delete(`/community/articles/${articleId}`)
  }

  async publishArticle(articleId: number): Promise<Article> {
    const response = await this.client.post<Article>(`/community/articles/${articleId}/publish`)
    return response.data
  }

  // Community - Comments
  async getComments(articleId: number): Promise<Comment[]> {
    const response = await this.client.get<Comment[]>(`/community/articles/${articleId}/comments`)
    return response.data
  }

  async createComment(articleId: number, data: CommentCreate): Promise<Comment> {
    const response = await this.client.post<Comment>(
      `/community/articles/${articleId}/comments`,
      data
    )
    return response.data
  }

  async updateComment(commentId: number, content: string): Promise<Comment> {
    const response = await this.client.put<Comment>(`/community/comments/${commentId}`, {
      content,
    })
    return response.data
  }

  async deleteComment(commentId: number): Promise<void> {
    await this.client.delete(`/community/comments/${commentId}`)
  }

  // Community - Likes
  async toggleArticleLike(articleId: number): Promise<LikeResponse> {
    const response = await this.client.post<LikeResponse>(`/community/articles/${articleId}/like`)
    return response.data
  }

  async toggleCommentLike(commentId: number): Promise<LikeResponse> {
    const response = await this.client.post<LikeResponse>(`/community/comments/${commentId}/like`)
    return response.data
  }

  // Community - Follows
  async toggleFollow(userId: number): Promise<FollowResponse> {
    const response = await this.client.post<FollowResponse>(`/community/users/${userId}/follow`)
    return response.data
  }

  async getUserProfile(userId: number): Promise<UserProfile> {
    const response = await this.client.get<UserProfile>(`/community/users/${userId}/profile`)
    return response.data
  }

  // Community - Feed
  async getFeed(params?: { page?: number; page_size?: number }): Promise<ArticleListResponse> {
    const response = await this.client.get<ArticleListResponse>('/community/feed', { params })
    return response.data
  }

  // Dividend Calendar
  async getDividendCalendar(
    portfolioId: number,
    params?: { months_ahead?: number; include_past?: boolean }
  ): Promise<DividendCalendarResponse> {
    const response = await this.client.get<DividendCalendarResponse>(
      `/analytics/portfolio/${portfolioId}/dividends/calendar`,
      { params }
    )
    return response.data
  }

  async getMonthlyDividends(
    portfolioId: number,
    year?: number
  ): Promise<MonthlyDividendResponse> {
    const response = await this.client.get<MonthlyDividendResponse>(
      `/analytics/portfolio/${portfolioId}/dividends/monthly`,
      { params: year ? { year } : undefined }
    )
    return response.data
  }

  // Subscriptions
  async getPlans(): Promise<Plan[]> {
    const response = await this.client.get<Plan[]>('/subscriptions/plans')
    return response.data
  }

  async getSubscription(): Promise<SubscriptionSummary> {
    const response = await this.client.get<SubscriptionSummary>('/subscriptions/subscription')
    return response.data
  }

  async getSubscriptionDetails(): Promise<Subscription> {
    const response = await this.client.get<Subscription>('/subscriptions/subscription/details')
    return response.data
  }

  async createCheckout(
    planId: number,
    billingPeriod: BillingPeriod
  ): Promise<CloudPaymentsCheckoutData> {
    const response = await this.client.post<CloudPaymentsCheckoutData>(
      '/subscriptions/subscription/checkout',
      { plan_id: planId, billing_period: billingPeriod }
    )
    return response.data
  }

  async cancelSubscription(
    immediately: boolean = false,
    reason?: string
  ): Promise<{ message: string; expires_at: string }> {
    const response = await this.client.post('/subscriptions/subscription/cancel', {
      immediately,
      reason,
    })
    return response.data
  }

  async getPayments(
    skip: number = 0,
    limit: number = 20
  ): Promise<PaymentListResponse> {
    const response = await this.client.get<PaymentListResponse>('/subscriptions/payments', {
      params: { skip, limit },
    })
    return response.data
  }

  async getPayment(paymentId: number): Promise<Payment> {
    const response = await this.client.get<Payment>(`/subscriptions/payments/${paymentId}`)
    return response.data
  }

  // Other Assets
  async getOtherAssetCategories(): Promise<CategorySubcategories[]> {
    const response = await this.client.get<CategorySubcategories[]>('/other-assets/categories')
    return response.data
  }

  async getOtherAssetCategoryLabels(): Promise<Record<string, string>> {
    const response = await this.client.get<Record<string, string>>('/other-assets/category-labels')
    return response.data
  }

  async getOtherAssets(
    portfolioId: number,
    category?: OtherAssetCategory
  ): Promise<OtherAsset[]> {
    const response = await this.client.get<OtherAsset[]>(
      `/other-assets/portfolio/${portfolioId}`,
      { params: category ? { category } : undefined }
    )
    return response.data
  }

  async getOtherAssetsSummary(portfolioId: number): Promise<OtherAssetsSummary> {
    const response = await this.client.get<OtherAssetsSummary>(
      `/other-assets/portfolio/${portfolioId}/summary`
    )
    return response.data
  }

  async createOtherAsset(
    portfolioId: number,
    data: OtherAssetCreate
  ): Promise<OtherAsset> {
    const response = await this.client.post<OtherAsset>(
      `/other-assets/portfolio/${portfolioId}`,
      data
    )
    return response.data
  }

  async getOtherAsset(assetId: number): Promise<OtherAssetWithIncome> {
    const response = await this.client.get<OtherAssetWithIncome>(
      `/other-assets/${assetId}`
    )
    return response.data
  }

  async updateOtherAsset(
    assetId: number,
    data: OtherAssetUpdate
  ): Promise<OtherAsset> {
    const response = await this.client.put<OtherAsset>(
      `/other-assets/${assetId}`,
      data
    )
    return response.data
  }

  async deleteOtherAsset(assetId: number): Promise<void> {
    await this.client.delete(`/other-assets/${assetId}`)
  }

  async refreshOtherAssetValuation(assetId: number): Promise<OtherAsset> {
    const response = await this.client.post<OtherAsset>(
      `/other-assets/${assetId}/refresh-valuation`
    )
    return response.data
  }

  // Income Records (for royalty assets)
  async getIncomeRecords(
    assetId: number,
    year?: number,
    limit: number = 100
  ): Promise<IncomeRecord[]> {
    const response = await this.client.get<IncomeRecord[]>(
      `/other-assets/${assetId}/income`,
      { params: { year, limit } }
    )
    return response.data
  }

  async addIncomeRecord(
    assetId: number,
    data: IncomeRecordCreate
  ): Promise<IncomeRecord> {
    const response = await this.client.post<IncomeRecord>(
      `/other-assets/${assetId}/income`,
      data
    )
    return response.data
  }

  async deleteIncomeRecord(assetId: number, recordId: number): Promise<void> {
    await this.client.delete(`/other-assets/${assetId}/income/${recordId}`)
  }

  // Pipelines
  async getPipelines(): Promise<Pipeline[]> {
    const response = await this.client.get<Pipeline[]>('/pipelines')
    return response.data
  }

  async createPipeline(data: PipelineCreate): Promise<Pipeline> {
    const response = await this.client.post<Pipeline>('/pipelines', data)
    return response.data
  }

  async getPipeline(id: number): Promise<PipelineDetail> {
    const response = await this.client.get<PipelineDetail>(`/pipelines/${id}`)
    return response.data
  }

  async updatePipeline(id: number, data: PipelineUpdate): Promise<Pipeline> {
    const response = await this.client.put<Pipeline>(`/pipelines/${id}`, data)
    return response.data
  }

  async deletePipeline(id: number): Promise<void> {
    await this.client.delete(`/pipelines/${id}`)
  }

  async getKanbanBoard(pipelineId: number): Promise<KanbanBoard> {
    const response = await this.client.get<KanbanBoard>(`/pipelines/${pipelineId}/kanban`)
    return response.data
  }

  async getPipelineStats(pipelineId: number): Promise<PipelineStats> {
    const response = await this.client.get<PipelineStats>(`/pipelines/${pipelineId}/stats`)
    return response.data
  }

  // Pipeline Stages
  async getStages(pipelineId: number): Promise<PipelineStage[]> {
    const response = await this.client.get<PipelineStage[]>(`/pipelines/${pipelineId}/stages`)
    return response.data
  }

  async createStage(pipelineId: number, data: PipelineStageCreate): Promise<PipelineStage> {
    const response = await this.client.post<PipelineStage>(
      `/pipelines/${pipelineId}/stages`,
      data
    )
    return response.data
  }

  async updateStage(
    pipelineId: number,
    stageId: number,
    data: PipelineStageUpdate
  ): Promise<PipelineStage> {
    const response = await this.client.put<PipelineStage>(
      `/pipelines/${pipelineId}/stages/${stageId}`,
      data
    )
    return response.data
  }

  async deleteStage(pipelineId: number, stageId: number): Promise<void> {
    await this.client.delete(`/pipelines/${pipelineId}/stages/${stageId}`)
  }

  async reorderStages(pipelineId: number, stageIds: number[]): Promise<PipelineStage[]> {
    const response = await this.client.post<PipelineStage[]>(
      `/pipelines/${pipelineId}/stages/reorder`,
      { stage_ids: stageIds }
    )
    return response.data
  }

  // Pipeline Access
  async getPipelineAccess(pipelineId: number): Promise<PipelineAccess[]> {
    const response = await this.client.get<PipelineAccess[]>(`/pipelines/${pipelineId}/access`)
    return response.data
  }

  async grantPipelineAccess(
    pipelineId: number,
    data: PipelineAccessCreate
  ): Promise<PipelineAccess> {
    const response = await this.client.post<PipelineAccess>(
      `/pipelines/${pipelineId}/access`,
      data
    )
    return response.data
  }

  async updatePipelineAccess(
    pipelineId: number,
    accessId: number,
    accessLevel: AccessLevel
  ): Promise<PipelineAccess> {
    const response = await this.client.put<PipelineAccess>(
      `/pipelines/${pipelineId}/access/${accessId}`,
      { access_level: accessLevel }
    )
    return response.data
  }

  async revokePipelineAccess(pipelineId: number, accessId: number): Promise<void> {
    await this.client.delete(`/pipelines/${pipelineId}/access/${accessId}`)
  }

  // Deals
  async getDeals(params: {
    pipeline_id: number
    stage_id?: number
    status?: DealStatus
    assigned_to?: number
    search?: string
    page?: number
    page_size?: number
  }): Promise<DealListResponse> {
    const response = await this.client.get<DealListResponse>('/deals', { params })
    return response.data
  }

  async createDeal(data: DealCreate): Promise<Deal> {
    const response = await this.client.post<Deal>('/deals', data)
    return response.data
  }

  async getDeal(id: number): Promise<DealDetail> {
    const response = await this.client.get<DealDetail>(`/deals/${id}`)
    return response.data
  }

  async updateDeal(id: number, data: DealUpdate): Promise<Deal> {
    const response = await this.client.put<Deal>(`/deals/${id}`, data)
    return response.data
  }

  async deleteDeal(id: number): Promise<void> {
    await this.client.delete(`/deals/${id}`)
  }

  async moveDeal(id: number, data: DealMoveRequest): Promise<Deal> {
    const response = await this.client.post<Deal>(`/deals/${id}/move`, data)
    return response.data
  }

  async reorderDeals(dealId: number, dealIds: number[]): Promise<void> {
    await this.client.post(`/deals/${dealId}/reorder`, { deal_ids: dealIds })
  }

  // Deal Contacts
  async getDealContacts(dealId: number): Promise<DealContact[]> {
    const response = await this.client.get<DealContact[]>(`/deals/${dealId}/contacts`)
    return response.data
  }

  async createDealContact(dealId: number, data: DealContactCreate): Promise<DealContact> {
    const response = await this.client.post<DealContact>(`/deals/${dealId}/contacts`, data)
    return response.data
  }

  async updateDealContact(
    dealId: number,
    contactId: number,
    data: Partial<DealContactCreate>
  ): Promise<DealContact> {
    const response = await this.client.put<DealContact>(
      `/deals/${dealId}/contacts/${contactId}`,
      data
    )
    return response.data
  }

  async deleteDealContact(dealId: number, contactId: number): Promise<void> {
    await this.client.delete(`/deals/${dealId}/contacts/${contactId}`)
  }

  // Deal Documents
  async getDealDocuments(dealId: number): Promise<DealDocument[]> {
    const response = await this.client.get<DealDocument[]>(`/deals/${dealId}/documents`)
    return response.data
  }

  async createDealDocument(dealId: number, data: DealDocumentCreate): Promise<DealDocument> {
    const response = await this.client.post<DealDocument>(`/deals/${dealId}/documents`, data)
    return response.data
  }

  async deleteDealDocument(dealId: number, documentId: number): Promise<void> {
    await this.client.delete(`/deals/${dealId}/documents/${documentId}`)
  }

  // Deal Comments
  async getDealComments(dealId: number): Promise<DealComment[]> {
    const response = await this.client.get<DealComment[]>(`/deals/${dealId}/comments`)
    return response.data
  }

  async createDealComment(dealId: number, data: DealCommentCreate): Promise<DealComment> {
    const response = await this.client.post<DealComment>(`/deals/${dealId}/comments`, data)
    return response.data
  }

  async updateDealComment(
    dealId: number,
    commentId: number,
    content: string
  ): Promise<DealComment> {
    const response = await this.client.put<DealComment>(
      `/deals/${dealId}/comments/${commentId}`,
      { content }
    )
    return response.data
  }

  async deleteDealComment(dealId: number, commentId: number): Promise<void> {
    await this.client.delete(`/deals/${dealId}/comments/${commentId}`)
  }

  // Deal Activities
  async getDealActivities(dealId: number, limit: number = 50): Promise<DealActivity[]> {
    const response = await this.client.get<DealActivity[]>(`/deals/${dealId}/activities`, {
      params: { limit },
    })
    return response.data
  }

  // Deals CSV Import/Export
  async exportDeals(pipelineId: number): Promise<Blob> {
    const response = await this.client.get(`/deals/export`, {
      params: { pipeline_id: pipelineId },
      responseType: 'blob',
    })
    return response.data
  }

  async importDeals(pipelineId: number, file: File): Promise<ImportResult> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.client.post<ImportResult>('/deals/import', formData, {
      params: { pipeline_id: pipelineId },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  async getDealsImportTemplate(): Promise<Blob> {
    const response = await this.client.get('/deals/import/template', {
      responseType: 'blob',
    })
    return response.data
  }
}

export const api = new ApiService()
